/**
 * Summer jobs resource article URLs (per locale). Legacy URLs may still include jobType query params;
 * see `mergeSummerJobsFilterIntoUrl` and `getSummerJobsSearchHref` for the filtered SRP link.
 */

import {
  inferAppLocaleFromPathname,
  summerJobTypeFacetForLocale,
} from '../job/summer-job-job-type';

const EN_SLUG = 'summer-jobs';
const FI_SLUG = 'kesatyot';
const SV_SLUG = 'sommarjobb-i-finland';

export type SummerJobsLocale = 'en' | 'fi' | 'sv';

export const SUMMER_JOBS_PATHS: Record<SummerJobsLocale, string> = {
  en: `/en/resources/${EN_SLUG}`,
  fi: `/fi/tyoelama/${FI_SLUG}`,
  sv: `/sv/arbetsliv/${SV_SLUG}`,
};

/** Path to the summer jobs article without the `/{locale}` prefix (for localizeRoute). */
export function localizedSummerJobsArticlePath(locale: SummerJobsLocale): string {
  return SUMMER_JOBS_PATHS[locale].replace(/^\/(en|fi|sv)/, '');
}

/**
 * Normalized pathname (no trailing slash), with optional locale prefix.
 */
export function isSummerJobsResourcePathname(pathname: string): boolean {
  const p = (pathname || '').replace(/\/+$/, '') || '/';
  const segments = p.split('/').filter(Boolean);
  const isLocale = segments[0] && ['en', 'fi', 'sv'].includes(segments[0].toLowerCase());
  const rest = isLocale ? segments.slice(1) : segments;
  if (rest.length !== 2) return false;
  const [a, b] = rest.map((s) => s.toLowerCase());
  if (a === 'resources' && b === EN_SLUG) return true;
  if (a === 'tyoelama' && b === FI_SLUG) return true;
  if (a === 'arbetsliv' && b === SV_SLUG) return true;
  return false;
}

function summerLandingHasSummerJobType(params: URLSearchParams, pathname: string): boolean {
  const expected = summerJobTypeFacetForLocale(inferAppLocaleFromPathname(pathname));
  return params.getAll('jobType').some((v) => decodeURIComponent(v) === expected);
}

/** Merge Algolia query param so SSR + URL parsing apply the summer job filter (via jobType facet). */
export function mergeSummerJobsFilterIntoUrl(urlOriginal: string): string {
  let u: URL;
  try {
    u = new URL(urlOriginal, 'http://localhost');
  } catch {
    return urlOriginal;
  }
  if (!isSummerJobsResourcePathname(u.pathname)) {
    return urlOriginal;
  }
  u.searchParams.delete('isSummerJob');
  if (!summerLandingHasSummerJobType(u.searchParams, u.pathname)) {
    u.searchParams.append('jobType', summerJobTypeFacetForLocale(inferAppLocaleFromPathname(u.pathname)));
  }
  const qs = u.searchParams.toString();
  return `${u.pathname}${qs ? `?${qs}` : ''}`;
}
